import { FormApi } from "final-form";
import { observer } from "mobx-react-lite";
import { useContext } from "react";
import { useField, useForm, useFormState } from "react-final-form";

import { DateTime } from "@bps/date-time";
import { DateRangeType, Stack, useTheme } from "@bps/fluent-ui";
import { StatementItemsContext } from "@modules/billing/screens/statement/context/StatementItemsContext.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DatePickerField } from "@ui-components/form/DatePickerField.tsx";
import { FieldCondition } from "@ui-components/form/FieldCondition.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";
import { SpinNumberInputField } from "@ui-components/form/SpinNumberInputField.tsx";
import { SubmissionFormDialogHeader } from "@ui-components/form/submission-form-dialog/SubmissionFormDialogHeader.tsx";
import { FormSubmitButtons } from "@ui-components/form/submission-form/FormSubmitButtons.tsx";
import { YesNoToggleField } from "@ui-components/form/Toggle/YesNoToggleField.tsx";

import { StatementExpanderButton } from "./StatementExpanderButton.tsx";
import { StatementGenerateFormValues } from "./StatementGenerateModal.type.ts";
import { StatementLastGeneratedMessageBar } from "./StatementLastGeneratedMessageBar.tsx";
import { StatementValueFilter } from "./StatementValueFilter.tsx";

interface StatementGenerateModalFieldsProps {
  disabled?: boolean;
}

export const StatementGenerateModalFields: React.FC<StatementGenerateModalFieldsProps> =
  observer(() => {
    const {
      statementGenerateFormNameOf,
      handleSubmitAction,
      selectedIds,
      setIsStatementNewModalVisible
    } = useContext(StatementItemsContext);

    const { values } = useFormState<StatementGenerateFormValues>();

    const { billing } = useStores();
    const { spacing } = useTheme();

    const onHandleSubmit = async (
      form: FormApi<StatementGenerateFormValues>
    ) => {
      await handleSubmitAction(form.getState().values);
      setIsStatementNewModalVisible(false);
    };

    const {
      input: { value: endDate }
    } = useField<Date>(statementGenerateFormNameOf("endDate"), {
      subscription: { value: true }
    });

    const {
      input: { value: gapDays }
    } = useField<number>(statementGenerateFormNameOf("gapDays"), {
      subscription: { value: true }
    });

    const form = useForm<StatementGenerateFormValues>();

    return (
      <>
        <SubmissionFormDialogHeader
          onCancel={() => setIsStatementNewModalVisible(false)}
          title="Statement"
        />
        <Stack
          styles={{
            root: {
              overflowY: "auto",
              padding: "0 24px",
              flex: 1
            }
          }}
        >
          <Stack tokens={{ childrenGap: 8 }}>
            <StatementLastGeneratedMessageBar />
            <Stack tokens={{ childrenGap: 4 }}>
              <DatePickerField
                name={statementGenerateFormNameOf("endDate")}
                maxDate={DateTime.jsDateNow()}
                placeholder="Date(s)"
                required
                label="Accounts as at"
                dateRangeType={DateRangeType.Day}
                validateOnInitialize
                allowClear
              />
              <SpinNumberInputField
                label="Show transactions & activity"
                name={statementGenerateFormNameOf("gapDays")}
                max={120}
                min={1}
                styles={{ root: { width: 90 } }}
                fieldItemStyles={{
                  root: { flexGrow: 2.8, flexBasis: 0 },
                  item: { flexGrow: 0, width: 90 }
                }}
                parse={value => Number(value)}
                suffix="days"
                hint={
                  endDate
                    ? `${DateTime.fromJSDate(endDate)
                        .minus({ days: gapDays })
                        .toDayDefaultFormat()} - ${DateTime.fromJSDate(
                        endDate
                      ).toDayDefaultFormat()}`
                    : undefined
                }
              />
              <YesNoToggleField
                label="Filter by account value"
                name={statementGenerateFormNameOf("valueFilter")}
              />
              <FieldSpy
                name={statementGenerateFormNameOf("valueFilter")}
                onChange={(value: boolean) => {
                  if (
                    !value &&
                    (values.owingOver !== undefined ||
                      values.creditOver !== undefined)
                  ) {
                    form.change(
                      statementGenerateFormNameOf("owingOver"),
                      undefined
                    );
                    form.change(
                      statementGenerateFormNameOf("creditOver"),
                      undefined
                    );
                  }
                }}
              />
            </Stack>
          </Stack>
          <FieldCondition
            when={statementGenerateFormNameOf("valueFilter")}
            is={(value: boolean) => !!value}
          >
            <Stack.Item
              styles={{
                root: {
                  marginBottom: 8
                }
              }}
            >
              <StatementValueFilter />
            </Stack.Item>
          </FieldCondition>
          <StatementExpanderButton endDate={endDate} gapDays={gapDays} />
        </Stack>

        <FormSubmitButtons
          promptOnCancel
          hideButtonsSeparator
          onCancel={() => {
            setIsStatementNewModalVisible(false);
            billing.ui.setStatementSecondColumn(undefined);
          }}
          onSubmit={onHandleSubmit}
          submitButtonProps={{
            id: "submit-statement-form",
            text: "Generate",
            iconProps: undefined,
            disabled:
              selectedIds?.length === 0 || !endDate || !gapDays || gapDays > 120
          }}
          styles={{ root: { padding: spacing.l1 } }}
          cancelButtonProps={{ text: "Cancel" }}
        />
      </>
    );
  });
